import React, { useEffect, useState } from "react";
import {
  Layout,
  Avatar,
  Typography,
  Flex,
  Badge,
  Space,
  Button,
  Modal,
  List,
  Checkbox,
} from "antd";
import { useSelector } from "react-redux";
import { PhoneOutlined } from "@ant-design/icons";
import { GroupIcon, MegaphoneIcon } from "../../icons";
import { toast } from "react-toastify";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import { RootState } from "../../../redux/store";
import { storageService } from "../../../services/storageService";
import { updateCurrentChat } from "../../../redux/slices/chatSlice";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { makeCall, makeGroupCall } from "../../../redux/slices/sipSlice";
import { checkIfContactSaved } from "../../../utils";
import GradientButton from "../../common/UI/GradientButton";
import "./index.scss";

const { Header } = Layout;

interface TopHeaderProps {}

const ChatHeader: React.FC<TopHeaderProps> = () => {
  const dispatch = useAppDispatch();
  const { calls } = useSelector((state: RootState) => state.sip);
  const { Text } = Typography;
  const { contacts } = useSelector((state: RootState) => state.contacts);
  const { currentChat } = useSelector((state: RootState) => state.chat);
  const { currentScreen, setCurrentScreen } = useChatSidebarContext();
  const { callerId: callerIdValue } = useSelector(
    (state: RootState) => state.sip,
  );
  const [canCall, setCanCall] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [selectCallMembersModalOpen, setSelectCallMembersModalOpen] =
    useState(false);
  const userId = storageService.getUserId();

  const handleHeaderClick = () => {
    if (currentScreen === "group-info" || currentScreen === "broadcast-info")
      return;

    if (!currentChat?.isGroup && !currentChat?.isBroadCast) return;

    const chatWithPreviousScreenState = {
      ...currentChat,
      previousScreen: currentScreen,
    };

    dispatch(updateCurrentChat(chatWithPreviousScreenState));

    if (currentChat?.isGroup) {
      setCurrentScreen("group-info");
    } else if (currentChat?.isBroadCast) {
      setCurrentScreen("broadcast-info");
    }
  };

  const handleCheckboxChange = (memberId: string, checked: boolean) => {
    setSelectedMembers((prev) =>
      checked ? [...prev, memberId] : prev.filter((id) => id !== memberId),
    );
  };

  const handleCall = (e: any) => {
    e.preventDefault();

    if (currentChat?.isGroup) {
      const settings = currentChat?.groupSettings;
      const currentUser = currentChat?.groupMembers?.find(
        (member: any) => member?._id === userId,
      );

      if (currentUser?.isAdmin) {
        setSelectCallMembersModalOpen(true);
      } else if (!settings?.sendMessage) {
        toast.error("Only admins can initiate call");
      } else {
        setSelectCallMembersModalOpen(true);
      }
    } else {
      dispatch(
        makeCall({
          source: callerIdValue || "",
          target: currentChat?.number,
          callType: "1",
        }),
      );
    }
  };

  const handleGroupCall = () => {
    if (!selectedMembers.length || !currentChat?.id) {
      toast.error("Please select at least 1 member");
      return;
    }
    const user = currentChat?.groupMembers?.find(
      (item: any) => item._id === userId,
    );

    const allMemberIds = user
      ? [...selectedMembers, user._id].join(",")
      : selectedMembers.join(",");

    dispatch(
      makeGroupCall({
        source: callerIdValue || "",
        callType: "1",
        groupId: currentChat.id,
        memberIds: allMemberIds,
      }),
    );

    setSelectCallMembersModalOpen(false);
    setSelectedMembers([]);
  };

  useEffect(() => {
    if (!currentChat) {
      setCanCall(false);
      return;
    }

    const isGroup = currentChat?.isGroup;
    const isBroadCast = currentChat?.isBroadCast;
    const blocked = currentChat?.isBlocked;
    const alreadyInCall = Boolean(Object.keys(calls)?.length);

    let allowed = false;

    if (blocked) {
      allowed = false;
    } else if (alreadyInCall) {
      allowed = false;
    } else if (!isGroup && !isBroadCast) {
      allowed = true;
    } else if (isGroup) {
      // if (
      //   currentChat.groupMembers.find((member: any) => member._id === userId)
      // ) {
      //   allowed = true;
      // } else {
      //   allowed = false;
      // }
      allowed = true;
    } else if (isBroadCast) {
      allowed = false;
    } else {
      allowed = true;
    }

    setCanCall(allowed);
  }, [calls, currentChat, userId]);

  return (
    <Header
      className="chat-feed-header"
      onClick={handleHeaderClick}
      style={{
        cursor:
          currentChat?.isGroup || currentChat?.isBroadCast
            ? "pointer"
            : "default",
      }}
    >
      <Avatar src={currentChat?.image} className="avatar">
        {currentChat?.isBroadCast ? (
          <div className="broadcast-icon">
            <MegaphoneIcon />
          </div>
        ) : currentChat?.isGroup ? (
          <div className="group-icon">
            <GroupIcon />
          </div>
        ) : currentChat?.savedName ? (
          <Text className="initial">
            {currentChat?.savedName.charAt(0).toUpperCase()}
          </Text>
        ) : currentChat?.name ? (
          <Text className="initial">
            {currentChat.name.charAt(0).toUpperCase()}
          </Text>
        ) : (
          "+"
        )}
      </Avatar>

      <Flex align="baseline" gap={10}>
        <Flex vertical>
          <Space size={4} className="chat-title">
            {currentChat
              ? currentChat.isGroup || currentChat.isBroadCast
                ? currentChat.name
                : currentChat.savedName
                  ? currentChat.savedName
                  : currentChat.name
              : "Select a chat"}{" "}
            {currentChat &&
              !currentChat.isGroup &&
              !currentChat.isBroadCast &&
              currentChat.iseFone && <Badge className="free-badge">Free</Badge>}
          </Space>
          {!currentChat?.isGroup &&
            !currentChat?.isBroadCast &&
            currentChat?.savedName !== currentChat?.sourceNumber &&
            currentChat?.savedName?.slice(0, 1) !== "+" && (
              <Text className="source-number">{currentChat?.sourceNumber}</Text>
            )}
        </Flex>

        <Text className="chat-info">
          {currentChat?.isGroup
            ? `${currentChat?.groupMembers?.length ?? 0} Members`
            : currentChat?.isBroadCast
              ? `${currentChat?.broadCastMembers?.length ?? 0} Members`
              : ""}
        </Text>
      </Flex>

      {canCall && (
        <Button
          type="primary"
          className="call-btn"
          onClick={handleCall}
          icon={<PhoneOutlined />}
        />
      )}

      <Modal
        centered
        open={selectCallMembersModalOpen}
        onCancel={() => setSelectCallMembersModalOpen(false)}
        className="custom-modal select-call-members-modal"
        footer={null}
        closeIcon={null}
        title="Select Members"
      >
        <List
          className="members-list"
          dataSource={currentChat?.groupMembers?.filter(
            (item: any) => item._id !== userId,
          ) || []}
          renderItem={(item: any) => (
            <div className="gc-members-wrapper" key={item._id}>
              <div>{checkIfContactSaved(item.phoneNumber, contacts)}</div>
              <Checkbox
                checked={selectedMembers.includes(item._id)}
                onChange={(e) =>
                  handleCheckboxChange(item._id, e.target.checked)
                }
              />
            </div>
          )}
        />
        <GradientButton onClick={handleGroupCall}>Call</GradientButton>
      </Modal>
    </Header>
  );
};

export default ChatHeader;
